import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';

import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  Observable,
  Subject,
  switchMap,
  take
} from 'rxjs';

import { AccessTokenService } from 'src/app/core/data-access/access-token.service';
import { environment } from 'src/environments/environment';
import {
  ExpertModePromptBody,
  PromptRequestResponse
} from '../model/expert-mode.model';

@Injectable({
  providedIn: 'root'
})
export class ExpertModeService implements OnDestroy {
  private httpClient = inject(HttpClient);
  private accessTokenService = inject(AccessTokenService);
  private translate = inject(TranslateService);
  private snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();

  userPromptText$ = new BehaviorSubject<string | undefined>(undefined);
  defaultPromptText$ = new BehaviorSubject<string | undefined>(undefined);

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setDefaultPrompt() {
    const name = 'DEFAULT_SYSTEM_PROMPT';
    this.getDefaultPrompt(name)
      .pipe(take(1))
      .subscribe(response => {
        this.defaultPromptText$.next(
          this.removeAfterSpecialChar(response.prompt)
        );

        this.setPrompt(response.prompt, true);
      });
  }

  getDefaultPrompt(name: string) {
    const url = `${environment.inferenceBackendUrl}/get_prompt_from_langfuse?name=DEFAULT_SYSTEM_PROMPT`;

    return this.httpClient.get<PromptRequestResponse>(url, {
      params: { name },
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  setPrompt(prompt: string, isDefaultPrompt?: boolean) {
    const name = 'EXPERT_MODE';

    return this.postPrompt(prompt, name)
      .pipe(
        take(1),
        switchMap(response => {
          console.log({ response });
          if (response.status_code !== 200) {
            this.showErrorSnackbar('settings.expertModeGetDefaultError');
            throw new Error(`${response.message} ${response.status_code}`);
          }

          this.userPromptText$.next(this.removeAfterSpecialChar(prompt));

          if (!isDefaultPrompt) {
            this.showSuccessSnackbar('settings.expertModeSetPromptSuccess');
          }

          return [];
        }),
        catchError(err => {
          this.showErrorSnackbar('settings.expertModeGetDefaultError');
          throw err;
        })
      )
      .subscribe();
  }

  postPrompt(prompt: string, name: string): Observable<PromptRequestResponse> {
    const url = `${environment.inferenceBackendUrl}/set_prompt`;
    const body: ExpertModePromptBody = {
      name,
      prompt,
      session_id: this.accessTokenService.activeAccountEmailUsername$.value
    };

    return this.httpClient.post<PromptRequestResponse>(url, body, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  private removeAfterSpecialChar(input: string): string {
    const specialCharIndex = input.indexOf('¦');
    return specialCharIndex !== -1
      ? input.substring(0, specialCharIndex)
      : input;
  }

  private showErrorSnackbar(messageKey: string) {
    this.snackBar.open(
      this.translate.instant(messageKey),
      this.translate.instant('buttons.close'),
      {
        panelClass: ['mat-snackbar-error'],
        duration: 3000
      }
    );
  }

  private showSuccessSnackbar(messageKey: string) {
    this.snackBar.open(
      this.translate.instant(messageKey),
      this.translate.instant('buttons.close'),
      {
        panelClass: ['mat-snackbar-success'],
        duration: 3000
      }
    );
  }
}
